// ABOUTME: One application on the "my applications" list: the opening title and company, "moved today" when it did, the
// ABOUTME: compact step bar with the current step under it while it runs, and the outcome pill with the reason once it ended.
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { BoldChip, BoldChipColors } from 'components/BoldChip';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import {
  TalentApplication,
  TalentApplicationStatus,
  TalentApplicationStepState
} from '../types';
import { daysSince, movedToday } from '../utils';
import { StepBar } from './StepBar';
import { StepTracker } from './StepTracker';

/** The pill's tone per status; the words on it come from the backend (see below). */
const STATUS_COLOR: Record<TalentApplicationStatus, BoldChipColors> = {
  [TalentApplicationStatus.ACTIVE]: 'info',
  [TalentApplicationStatus.HIRED]: 'success',
  [TalentApplicationStatus.REJECTED]: 'error',
  [TalentApplicationStatus.WITHDRAWN]: 'neutral'
};

/**
 * Only for the case the backend leaves the current step unnamed (the application sits in a step that is not shown to
 * talents). These name the four statuses, never a step: step names live in the process definition on the backend.
 */
const STATUS_LABEL: Record<TalentApplicationStatus, TranslationKey> = {
  [TalentApplicationStatus.ACTIVE]: 'applications.status.active',
  [TalentApplicationStatus.HIRED]: 'applications.status.hired',
  [TalentApplicationStatus.REJECTED]: 'applications.status.rejected',
  [TalentApplicationStatus.WITHDRAWN]: 'applications.status.withdrawn'
};

/**
 * PJL: what the application waits for from the talent, news since they last opened it, and that they hid it. Each only
 * when pro-core sent it, so an application from before PJL shows none of them.
 */
const PjlChips = ({ application }: { application: TalentApplication }) => {
  const { needsAction, hasNews, hidden } = application;
  if (!needsAction && !hasNews && !hidden) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
      {needsAction && (
        <BoldChip
          variant="soft"
          size="small"
          color="warning"
          data-testid="application-needs-action"
          data-type={needsAction.type}
          label={getTrans(
            `applications.needsAction.${needsAction.type}` as TranslationKey
          )}
        />
      )}
      {hasNews && (
        <BoldChip
          variant="soft"
          size="small"
          color="primary"
          data-testid="application-news"
          label={getTrans('applications.news')}
        />
      )}
      {hidden && (
        <BoldChip
          variant="soft"
          size="small"
          color="neutral"
          data-testid="application-hidden"
          label={getTrans('applications.hidden')}
        />
      )}
    </Box>
  );
};

/**
 * PJL: where the application stands, for the badge row of the job page's header: the current step (or the outcome,
 * in its colour), "moved today", news and hidden. The needs-you reminder is a notice on that page, not a badge.
 */
export const ApplicationStateBadges = ({
  application,
  now = new Date()
}: {
  application: TalentApplication;
  now?: Date;
}) => {
  const { status, currentStepLabel, hasNews, hidden } = application;
  const isActive = status === TalentApplicationStatus.ACTIVE;
  return (
    <>
      <BoldChip
        variant="soft"
        size="small"
        color={isActive ? 'primary' : STATUS_COLOR[status]}
        label={currentStepLabel ?? getTrans(STATUS_LABEL[status])}
        data-testid="application-step-badge"
        data-status={status}
      />
      {movedToday(application, now) && (
        <BoldChip
          variant="soft"
          size="small"
          label={getTrans('applications.card.movedToday')}
          color="primary"
          data-testid="application-moved-today"
        />
      )}
      {hasNews && (
        <BoldChip
          variant="soft"
          size="small"
          color="primary"
          data-testid="application-news"
          label={getTrans('applications.news')}
        />
      )}
      {hidden && (
        <BoldChip
          variant="soft"
          size="small"
          color="neutral"
          data-testid="application-hidden"
          label={getTrans('applications.hidden')}
        />
      )}
    </>
  );
};

/**
 * @param tracker `bar` on the list, where two applications must fit a phone: the compact bar with the current step under
 *                it while the application runs. `full` on the detail, where the tracker earns its height: the vertical
 *                tracker with every step, whatever the status.
 * @param framed  `true` on its own, a card with a frame; `false` as a row of the applications block, plain content the
 *                block separates with hairlines.
 * @param now     the instant "today" is measured from, injectable so a test does not fake timers. The Berlin day of the
 *                latest move against the Berlin day of `now` decides the chip and the day count.
 */
export const ApplicationCard = ({
  application,
  tracker = 'bar',
  framed = true,
  now = new Date()
}: {
  application: TalentApplication;
  tracker?: 'bar' | 'full';
  /** `false` inside the applications block, where the rows share one card and hairlines instead of frames of their own. */
  framed?: boolean;
  now?: Date;
}) => {
  const {
    jobOpeningTitle,
    companyName,
    status,
    currentStepLabel,
    steps,
    lastChangedAt
  } = application;
  const isActive = status === TalentApplicationStatus.ACTIVE;
  const isMovedToday = movedToday(application, now);
  const currentStep = steps.find(
    step => step.state === TalentApplicationStepState.CURRENT
  );
  // While the application runs, the step label under the bar carries the state and the header's right slot is for
  // "moved today" alone. Once it has ended, the outcome pill takes the slot and reads the outcome step's label in the
  // talent's language, straight from the backend, falling back to the status when the step is not shown to talents.
  const pillLabel = currentStepLabel ?? getTrans(STATUS_LABEL[status]);
  const hint = [
    currentStep?.hint,
    isActive && lastChangedAt
      ? getTransWithParams('applications.card.day', {
          count: daysSince(lastChangedAt, now)
        })
      : null
  ]
    .filter(Boolean)
    .join(' · ');

  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1.5,
          mb: 1.5
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Text
            variant="subtitle1"
            component="h2"
            color="text.primary"
            sx={{ fontWeight: 700, lineHeight: 1.3 }}
          >
            {jobOpeningTitle}
          </Text>
          <Text variant="body2">{companyName}</Text>
          <PjlChips application={application} />
        </Box>
        {isMovedToday && (
          <BoldChip
            variant="soft"
            size="small"
            label={getTrans('applications.card.movedToday')}
            color="primary"
            data-testid="application-moved-today"
            sx={{ flexShrink: 0 }}
          />
        )}
        {!isActive && (
          <BoldChip
            variant="soft"
            size="small"
            label={pillLabel}
            color={STATUS_COLOR[status]}
            data-testid="application-status"
            data-status={status}
            sx={{ flexShrink: 0, maxWidth: '55%' }}
          />
        )}
      </Box>
      {application.sharedReason && (
        <Box
          data-testid="application-shared-reason"
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            backgroundColor: 'action.hover'
          }}
        >
          <Text
            variant="caption"
            component="p"
            sx={{ fontWeight: 600, textTransform: 'uppercase' }}
          >
            {getTrans('applications.card.sharedReason')}
          </Text>
          <Text variant="body2" color="text.primary">
            {application.sharedReason}
          </Text>
        </Box>
      )}
      {tracker === 'full' && <StepTracker steps={steps} />}
      {tracker === 'bar' && isActive && (
        <>
          <StepBar steps={steps} />
          <Box data-testid="application-current-step" sx={{ mt: 1.25 }}>
            <Text variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
              {currentStepLabel ?? getTrans(STATUS_LABEL[status])}
            </Text>
            {hint && (
              <Text variant="caption" color="text.secondary" component="p">
                {hint}
              </Text>
            )}
          </Box>
        </>
      )}
    </>
  );

  if (!framed) {
    return (
      <Box data-testid="application-card" sx={{ px: 2.5, py: 2 }}>
        {content}
      </Box>
    );
  }

  return (
    <Card
      data-testid="application-card"
      sx={{ borderRadius: 4, mb: 2, boxShadow: 'none' }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {content}
      </CardContent>
    </Card>
  );
};
